import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Send, CheckCircle, AlertCircle, Phone, ArrowLeft, ShieldCheck } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'

const FinancialForm = () => {
  const location = useLocation()
  const initialCategory = location.state?.category || ''

  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    businessName: '',
    businessType: initialCategory
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null) // 'success', 'error', null

  useEffect(() => {
    document.title = "Financial Inquiry Form - Shrijaa Finance";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Submit your financial inquiry to Shrijaa Finance. Let us know your requirements for Business Loans, MSME Loans, Home Loans, or Insurance, and we will get back to you shortly.");
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.origin + '/financial-services/apply');
  }, []);

  const categories = [
    'Business Loans',
    'MSME Loans',
    'Home Loans',
    'Auto Loans',
    'Personal Loans',
    'Gold Loans',
    'Education Loans',
    'Credit Cards',
    'Insurance',
    'Other / Custom Inquiry'
  ]

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      const response = await fetch('https://formspree.io/f/xykqqgko', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        setSubmitStatus('success')
        setFormData({
          name: '',
          mobile: '',
          businessName: '',
          businessType: ''
        })
      } else {
        setSubmitStatus('error')
      }
    } catch (error) {
      console.error('Error submitting financial inquiry:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleWhatsAppSubmit = () => {
    const { name, mobile, businessName, businessType } = formData
    if (!name || !mobile || !businessType) {
      alert('Please fill in the required fields (Name, Mobile, and Category) first.')
      return
    }
    const message = `Financial Inquiry:%0AName: ${encodeURIComponent(name)}%0AMobile: ${encodeURIComponent(mobile)}%0ABusiness Name: ${encodeURIComponent(businessName || '—')}%0ACategory/Type: ${encodeURIComponent(businessType)}`
    window.open(`https://wa.me/917989308337?text=${message}`, '_blank')
  }

  return (
    <div className="bg-gradient-to-b from-secondary-50 to-white min-h-screen py-12 md:py-16">
      <div className="container-max">
        {/* Back Link */}
        <div className="mb-6 max-w-2xl mx-auto">
          <Link to="/financial-services" className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Financial Services
          </Link>
        </div>

        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-xl border border-secondary-100/80 overflow-hidden"
          >
            {/* Header branding strip */}
            <div className="h-3 bg-gradient-to-r from-primary-600 via-teal-600 to-emerald-600" />
            
            <div className="p-8 md:p-10">
              <div className="text-center mb-8">
                <span className="text-accent-500 font-bold uppercase tracking-wider text-xs px-3 py-1 bg-accent-50 rounded-full">
                  Shrijaa Finance
                </span>
                <h1 className="text-3xl font-bold text-secondary-900 mt-3 mb-2">
                  Apply & Inquiry Form
                </h1>
                <p className="text-secondary-600 font-light">
                  Submit your details below to get instant callback and tailored assistance from our certified credit counselors.
                </p>
              </div>

              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 flex items-start gap-3"
                >
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold">Inquiry Sent Successfully!</h3>
                    <p className="text-sm text-green-700 mt-1">
                      Thank you for submitting your details. Our financial services team will review your application and reach out to you shortly.
                    </p>
                  </div>
                </motion.div>
              )}

              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 flex items-start gap-3"
                >
                  <AlertCircle className="w-6 h-6 text-rose-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold">Submission Failed</h3>
                    <p className="text-sm text-rose-700 mt-1">
                      There was a problem delivering your message via email. However, you can instantly submit the same details directly via WhatsApp below.
                    </p>
                  </div>
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-secondary-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="mobile" className="block text-sm font-semibold text-secondary-700 mb-2">
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="mobile"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    required
                    pattern="[0-9]{10,15}"
                    placeholder="e.g. 9876543210"
                    className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="businessName" className="block text-sm font-semibold text-secondary-700 mb-2">
                    Business Name <span className="text-secondary-500 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    id="businessName"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleChange}
                    placeholder="Enter your business or company name"
                    className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                  />
                </div>

                <div>
                  <label htmlFor="businessType" className="block text-sm font-semibold text-secondary-700 mb-2">
                    Type of Business / Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="businessType"
                    name="businessType"
                    value={formData.businessType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all bg-white"
                  >
                    <option value="" disabled>Select category or type</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-secondary-100">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold py-3.5 px-6 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Send className="w-5 h-5" />
                    {isSubmitting ? 'Submitting...' : 'Submit via Email'}
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleWhatsAppSubmit}
                    className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white font-semibold py-3.5 px-6 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <Phone className="w-5 h-5" />
                    Submit via WhatsApp
                  </button>
                </div>
              </form>

              <div className="mt-8 flex items-center justify-center gap-2 text-secondary-500 text-sm">
                <ShieldCheck className="w-5 h-5 text-primary-600" />
                <span>Your information is protected by industry-standard encryption.</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default FinancialForm
